import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Grid,
  CircularProgress,
  useMediaQuery,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { useGenerateTopicsMutation } from 'redux/api/topics.api';
import { TOPICS_CONSTANTS } from 'shared/constants/topics.constants';
import { setTopicUrl, setTopicError } from 'redux/slices/app/app.slice';
import { TextFieldWrapper } from 'components/Input/TextFieldWrapper';
import {
  TGenerateTopicsFormType,
  GenerateTopicsFormValidation,
  FormType,
} from './validationSchema';

export const GenerateTopicsForm: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [generateTopics] = useGenerateTopicsMutation();

  const [loadingState, setLoadingState] = React.useState<Record<FormType, boolean>>({
    [FormType.URL]: false,
    [FormType.TEXT]: false,
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    control: urlControl,
    handleSubmit: handleUrlSubmit,
    formState: { errors: urlErrors },
  } = useForm<TGenerateTopicsFormType>({
    defaultValues: {
      url: '',
    },
    resolver: zodResolver(GenerateTopicsFormValidation),
  });

  const {
    control: textControl,
    handleSubmit: handleTextSubmit,
    formState: { errors: textErrors },
  } = useForm<TGenerateTopicsFormType>({
    defaultValues: {
      text: '',
    },
    resolver: zodResolver(GenerateTopicsFormValidation),
  });

  const setLoading = (formType: FormType, isLoading: boolean) => {
    setLoadingState((prev) => ({
      ...prev,
      [formType]: isLoading,
    }));
  };

  const onSubmit = async (data: TGenerateTopicsFormType, formType: FormType) => {
    const alternativeErrorMessage =
      formType === FormType.URL
        ? TOPICS_CONSTANTS.ERROR_MESSAGE
        : TOPICS_CONSTANTS.URL_ERROR_MESSAGE;

    const payload = formType === FormType.URL ? { url: data.url } : { text: data.text };

    setLoading(formType, true);

    try {
      if (formType === FormType.URL) {
        dispatch(setTopicUrl(data.url || ''));
      }
      dispatch(setTopicError(null));
      await generateTopics(payload).unwrap();
    } catch (error: any) {
      let errorMessage = alternativeErrorMessage;
      if (error?.data?.text && error.data.text.length > 0) {
        const [firstError] = error.data.text;
        errorMessage = firstError;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      }
      dispatch(setTopicError(errorMessage));
    } finally {
      setLoading(formType, false);
    }
  };

  const onUrlSubmit: SubmitHandler<TGenerateTopicsFormType> = async (data) => {
    await onSubmit(data, FormType.URL);
  };

  const onTextSubmit: SubmitHandler<TGenerateTopicsFormType> = async (data) => {
    await onSubmit(data, FormType.TEXT);
  };

  // Check if any form is currently loading
  const isAnyFormLoading = Object.values(loadingState).some(Boolean);

  const renderMobileForm = () => (
    <>
      <Grid item>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          {TOPICS_CONSTANTS.URL_INPUT_LABEL}
        </Typography>
        <Box component="form" onSubmit={handleUrlSubmit(onUrlSubmit)}>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TextFieldWrapper
                name="url"
                control={urlControl}
                label=""
                placeholder={TOPICS_CONSTANTS.URL_INPUT_PLACEHOLDER}
                errorMessage={urlErrors.url ? urlErrors.url.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="maxSize"
                disabled={isAnyFormLoading}
                fullWidth
                sx={{
                  height: '40px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  fontSize: '14px',
                }}
              >
                {loadingState[FormType.URL] ? (
                  <CircularProgress size={20} />
                ) : (
                  TOPICS_CONSTANTS.GENERATE_FROM_URL_BUTTON_TEXT
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          {TOPICS_CONSTANTS.TEXT_INPUT_LABEL}
        </Typography>
        <Box component="form" onSubmit={handleTextSubmit(onTextSubmit)}>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TextFieldWrapper
                name="text"
                control={textControl}
                label=""
                placeholder={TOPICS_CONSTANTS.TEXT_INPUT_PLACEHOLDER}
                errorMessage={textErrors.text ? textErrors.text.message : ''}
                multiline
                minRows={1}
                maxRows={15}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="maxSize"
                disabled={isAnyFormLoading}
                fullWidth
                sx={{
                  height: '40px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  fontSize: '14px',
                }}
              >
                {loadingState[FormType.TEXT] ? (
                  <CircularProgress size={20} />
                ) : (
                  TOPICS_CONSTANTS.GENERATE_FROM_TEXT_BUTTON_TEXT
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </>
  );

  const renderDesktopForm = () => (
    <>
      <Grid item>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          {TOPICS_CONSTANTS.URL_INPUT_LABEL}
        </Typography>
        <Box component="form" onSubmit={handleUrlSubmit(onUrlSubmit)}>
          <Grid container spacing={2} direction="row" alignItems="flex-start">
            <Grid item xs>
              <TextFieldWrapper
                name="url"
                control={urlControl}
                label=""
                placeholder={TOPICS_CONSTANTS.URL_INPUT_PLACEHOLDER}
                errorMessage={urlErrors.url ? urlErrors.url.message : ''}
              />
            </Grid>
            <Grid item xs="auto">
              <Button
                type="submit"
                variant="maxSize"
                disabled={isAnyFormLoading}
                sx={{
                  height: '44px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  fontSize: '16px',
                  minWidth: '200px',
                }}
              >
                {loadingState[FormType.URL] ? (
                  <CircularProgress size={24} />
                ) : (
                  TOPICS_CONSTANTS.GENERATE_FROM_URL_BUTTON_TEXT
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          {TOPICS_CONSTANTS.TEXT_INPUT_LABEL}
        </Typography>
        <Box component="form" onSubmit={handleTextSubmit(onTextSubmit)}>
          <Grid container spacing={2} direction="row" alignItems="flex-start">
            <Grid item xs>
              <TextFieldWrapper
                name="text"
                control={textControl}
                label=""
                placeholder={TOPICS_CONSTANTS.TEXT_INPUT_PLACEHOLDER}
                errorMessage={textErrors.text ? textErrors.text.message : ''}
                multiline
                minRows={1}
                maxRows={15}
              />
            </Grid>
            <Grid item xs="auto">
              <Button
                type="submit"
                variant="maxSize"
                disabled={isAnyFormLoading}
                sx={{
                  height: '44px',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  fontSize: '16px',
                  minWidth: '200px',
                }}
              >
                {loadingState[FormType.TEXT] ? (
                  <CircularProgress size={24} />
                ) : (
                  TOPICS_CONSTANTS.GENERATE_FROM_TEXT_BUTTON_TEXT
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3} direction="column">
        {isMobile ? renderMobileForm() : renderDesktopForm()}
      </Grid>
    </Box>
  );
};

export default GenerateTopicsForm;
